// DropdownPortal.tsx (patch)
// - Close via backdrop onMouseDown (not document listener)
// - Stop propagation on menu container onMouseDown
// - Keep Escape to close

import * as React from "react";
import { createPortal } from "react-dom";

type DropdownPortalProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: React.ReactNode;
  minWidth?: number;
};

export const DropdownPortal: React.FC<DropdownPortalProps> = ({
  open,
  anchorEl,
  onClose,
  children,
  minWidth,
}) => {
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 0, height: 0 });
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  // Positioning
  React.useLayoutEffect(() => {
    if (!open || !anchorEl) return;
    const update = () => {
      const r = anchorEl.getBoundingClientRect();
      setPos({ top: r.bottom, left: r.left, width: r.width, height: r.height });
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(anchorEl);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorEl]);

  // Close on Escape
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Simple flip
  const viewportH = window.innerHeight;
  const estH = 360; // estimated height; not critical now
  const openUp = pos.top + estH > viewportH;
  const top = openUp ? pos.top - estH - pos.height : pos.top;

  return createPortal(
    // BACKDROP: handles outside click
    <div
      className="tw-fixed tw-inset-0 tw-z-[1000]" // pointer events enabled
      onMouseDown={() => onClose()}               // click on backdrop closes
      aria-hidden
    >
      {/* MENU: stop mouse down so it doesn't bubble to backdrop */}
      <div
        ref={menuRef}
        onMouseDown={(e) => e.stopPropagation()} // <- CRUCIAL
        className="tw-pointer-events-auto tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-xl tw-max-h-[70vh] tw-overflow-auto"
        style={{
          position: "fixed",
          top,
          left: pos.left,
          minWidth: minWidth ?? pos.width, // width is OK; not the cause
          maxWidth: "90vw",
          minHeight: '300px',
        }}
        role="dialog"
      >
        {children}
      </div>
    </div>,
    document.body
  );
};
